/**
 * BatchActionsDropdown Component
 *
 * Displays batch actions dropdown when transactions are selected.
 * Provides 7 batch operations: paid/unpaid, scheduled/unscheduled, dates, amount, account
 *
 * Phase 4.1: Added safety confirmations for destructive and important actions
 */

import { useState } from 'react';
import { ChevronDown, Check, X, Calendar, DollarSign, CreditCard, Clock } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { useBatchOperations } from '@/hooks/useBatchOperations';
import type { ConfirmationType } from '@/components/ui/confirmation-dialog';

export interface BatchActionsDropdownProps {
  selectedTransactions: Set<string>;
  onClearSelection: () => void;
  onOpenDateModal: () => void;
  onOpenAmountModal: () => void;
  onOpenAccountModal: () => void;
  confirm?: (type: ConfirmationType, onConfirm: () => void | Promise<void>, props?: any) => void;
}

export function BatchActionsDropdown({
  selectedTransactions,
  onClearSelection,
  onOpenDateModal,
  onOpenAmountModal,
  onOpenAccountModal,
  confirm,
}: BatchActionsDropdownProps) {
  const {
    batchMarkAsPaid,
    batchMarkAsUnpaid,
    batchUpdateScheduled,
    isUpdating,
  } = useBatchOperations();

  const [isOpen, setIsOpen] = useState(false);
  const selectedIds = Array.from(selectedTransactions);
  const count = selectedIds.length;

  if (count === 0) {
    return null;
  }

  // Show confirmation for important batch operations (>5 items) or always for unpaid
  const shouldConfirm = count > 5;

  const handleMarkAsPaid = () => {
    setIsOpen(false);

    const executeAction = async () => {
      await batchMarkAsPaid(selectedIds);
      onClearSelection();
    };

    if (shouldConfirm && confirm) {
      confirm('save', executeAction, {
        title: 'Confirmar Pagamento em Lote',
        description: `Tem certeza que deseja marcar ${count} transações como pagas? Esta ação irá definir a data de pagamento como hoje.`,
        confirmText: `Marcar ${count} como Pagas`,
        showDetails: true,
        details: [
          { label: 'Transações afetadas', value: count },
          { label: 'Ação', value: 'Definir como pago' },
          { label: 'Data de pagamento', value: new Date().toLocaleDateString('pt-BR') },
        ],
      });
    } else {
      executeAction();
    }
  };

  const handleMarkAsUnpaid = () => {
    setIsOpen(false);

    const executeAction = async () => {
      await batchMarkAsUnpaid(selectedIds);
      onClearSelection();
    };

    // Always confirm when marking as unpaid (reverses payment)
    if (confirm) {
      confirm('cancel', executeAction, {
        title: 'Confirmar Reversão de Pagamento',
        description: `Tem certeza que deseja marcar ${count} transações como não pagas? Esta ação irá remover as datas de pagamento e pode afetar a reconciliação bancária.`,
        confirmText: `Marcar ${count} como Não Pagas`,
        variant: 'warning',
        showDetails: true,
        details: [
          { label: 'Transações afetadas', value: count },
          { label: 'Ação', value: 'Reverter pagamento' },
          { label: 'Impacto', value: 'Saldos e relatórios serão recalculados' },
        ],
      });
    } else {
      executeAction();
    }
  };

  const handleMarkAsScheduled = () => {
    setIsOpen(false);

    const executeAction = async () => {
      await batchUpdateScheduled(selectedIds, true);
      onClearSelection();
    };

    if (shouldConfirm && confirm) {
      confirm('edit', executeAction, {
        title: 'Agendar Transações em Lote',
        description: `Marcar ${count} transações como agendadas?`,
        confirmText: `Agendar ${count} Transações`,
        showDetails: true,
        details: [
          { label: 'Transações afetadas', value: count },
          { label: 'Status', value: 'Agendado' },
        ],
      });
    } else {
      executeAction();
    }
  };

  const handleUnmarkAsScheduled = () => {
    setIsOpen(false);

    const executeAction = async () => {
      await batchUpdateScheduled(selectedIds, false);
      onClearSelection();
    };

    if (shouldConfirm && confirm) {
      confirm('edit', executeAction, {
        title: 'Desagendar Transações em Lote',
        description: `Remover agendamento de ${count} transações?`,
        confirmText: `Desagendar ${count} Transações`,
        showDetails: true,
        details: [
          { label: 'Transações afetadas', value: count },
          { label: 'Status', value: 'Não agendado' },
        ],
      });
    } else {
      executeAction();
    }
  };

  const handleOpenDateModal = () => {
    setIsOpen(false);
    onOpenDateModal();
  };

  const handleOpenAmountModal = () => {
    setIsOpen(false);
    onOpenAmountModal();
  };

  const handleOpenAccountModal = () => {
    setIsOpen(false);
    onOpenAccountModal();
  };

  return (
    <>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            disabled={isUpdating}
            className="gap-2"
          >
            Ações em lote ({count})
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-[240px]">
          <DropdownMenuItem onClick={handleMarkAsPaid}>
            <Check className="mr-2 h-4 w-4" />
            Definir como pago
          </DropdownMenuItem>

          <DropdownMenuItem onClick={handleMarkAsUnpaid}>
            <X className="mr-2 h-4 w-4" />
            Definir como em aberto
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem onClick={handleMarkAsScheduled}>
            <Clock className="mr-2 h-4 w-4" />
            Marcar como agendado
          </DropdownMenuItem>

          <DropdownMenuItem onClick={handleUnmarkAsScheduled}>
            <Clock className="mr-2 h-4 w-4 opacity-50" />
            Desmarcar como agendado
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem onClick={handleOpenDateModal}>
            <Calendar className="mr-2 h-4 w-4" />
            Alterar vencimento e/ou previsão
          </DropdownMenuItem>

          <DropdownMenuItem onClick={handleOpenAmountModal}>
            <DollarSign className="mr-2 h-4 w-4" />
            Alterar valor
          </DropdownMenuItem>

          <DropdownMenuItem onClick={handleOpenAccountModal}>
            <CreditCard className="mr-2 h-4 w-4" />
            Alterar conta
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
